import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DONE_STEP_ID,
  fetchCareerUpConfirmationProgress,
  upsertCareerUpConfirmationProgress,
} from './careerUpConfirmationApi';
import type {
  CareerUpConfirmationFormValues,
  CareerUpConfirmationStep,
  CareerUpConfirmationUploadMeta,
  YesNo,
} from './careerUpConfirmationTypes';

export type CareerUpConfirmationCommittedEntry = {
  stepIndex: number;
  stepId: CareerUpConfirmationStep;
  displayText: string;
  rawValue: string;
};

type StepDef = {
  stepId: CareerUpConfirmationStep;
  fieldName: keyof CareerUpConfirmationFormValues;
  kind: 'choice' | 'password';
};

const STEP_ORDER: CareerUpConfirmationStep[] = [
  'hasCard',
  'careerUpCardUploaded',
  'driversLicenseUploaded',
  'cardPassword',
];
const NEXT_ASSISTANT_QUESTION_DELAY_MS = 520;

const EMPTY_UPLOADS: CareerUpConfirmationUploadMeta = {
  vc008: { hasUpload: false },
  vc010: { hasUpload: false },
};

function stepOrdinal(stepId: CareerUpConfirmationStep): number {
  return STEP_ORDER.indexOf(stepId);
}

function isYes(raw: string): boolean {
  return raw.trim().toLowerCase() === 'yes';
}

function formatChoiceDisplay(
  t: (k: string) => string,
  stepId: CareerUpConfirmationStep,
  raw: string
): string {
  if (stepId === 'hasCard') {
    return isYes(raw)
      ? t('submitDocuments.careerUpConfirmation.hasCardYes')
      : t('submitDocuments.careerUpConfirmation.hasCardNo');
  }
  if (stepId === 'cardPassword') {
    return t('submitDocuments.careerUpConfirmation.passwordEntered');
  }
  return isYes(raw)
    ? t('submitDocuments.careerUpConfirmation.alreadyUploadedYes')
    : t('submitDocuments.careerUpConfirmation.notYetNo');
}

function buildSteps(values: CareerUpConfirmationFormValues): StepDef[] {
  const has = values.hasCareerUpCard;
  if (has === 'no') {
    return [{ stepId: 'hasCard', fieldName: 'hasCareerUpCard', kind: 'choice' }];
  }
  if (has !== 'yes') {
    return [{ stepId: 'hasCard', fieldName: 'hasCareerUpCard', kind: 'choice' }];
  }

  const steps: StepDef[] = [
    { stepId: 'hasCard', fieldName: 'hasCareerUpCard', kind: 'choice' },
    { stepId: 'careerUpCardUploaded', fieldName: 'careerUpCardUploaded', kind: 'choice' },
  ];
  if (values.careerUpCardUploaded === 'no') {
    return steps;
  }
  steps.push({
    stepId: 'driversLicenseUploaded',
    fieldName: 'driversLicenseUploaded',
    kind: 'choice',
  });
  if (values.driversLicenseUploaded === 'no') {
    return steps;
  }
  steps.push({ stepId: 'cardPassword', fieldName: 'cardPassword', kind: 'password' });
  return steps;
}

function cursorStepIdForSave(steps: StepDef[], stepIndex: number): string | null {
  if (stepIndex >= steps.length) return DONE_STEP_ID;
  return steps[stepIndex]?.stepId ?? null;
}

function upsertCommittedEntry(
  committed: CareerUpConfirmationCommittedEntry[],
  entry: CareerUpConfirmationCommittedEntry
): CareerUpConfirmationCommittedEntry[] {
  const next = [...committed];
  const idx = next.findIndex((c) => c.stepId === entry.stepId);
  if (idx >= 0) {
    next[idx] = entry;
    return next;
  }
  next.push(entry);
  return next;
}

function trimCommittedThrough(
  committed: CareerUpConfirmationCommittedEntry[],
  throughStepId: CareerUpConfirmationStep
): CareerUpConfirmationCommittedEntry[] {
  const max = stepOrdinal(throughStepId);
  return committed.filter((c) => stepOrdinal(c.stepId) <= max);
}

function clearDownstreamFields(
  values: CareerUpConfirmationFormValues,
  fromStepId: CareerUpConfirmationStep
): CareerUpConfirmationFormValues {
  const next = { ...values };
  if (stepOrdinal(fromStepId) < stepOrdinal('careerUpCardUploaded')) {
    delete next.careerUpCardUploaded;
  }
  if (stepOrdinal(fromStepId) < stepOrdinal('driversLicenseUploaded')) {
    delete next.driversLicenseUploaded;
  }
  if (stepOrdinal(fromStepId) < stepOrdinal('cardPassword')) {
    delete next.cardPassword;
  }
  return next;
}

type DeclinedKind = 'none' | 'noCard' | 'careerUpCardUploadNeeded' | 'driversLicenseUploadNeeded';

function declinedFromValues(values: CareerUpConfirmationFormValues): DeclinedKind {
  if (values.hasCareerUpCard === 'no') return 'noCard';
  if (values.careerUpCardUploaded === 'no') return 'careerUpCardUploadNeeded';
  if (values.driversLicenseUploaded === 'no') return 'driversLicenseUploadNeeded';
  return 'none';
}

function rebuildFromProgress(
  values: CareerUpConfirmationFormValues,
  currentStepId: string | null | undefined,
  t: (k: string) => string
): {
  stepIndex: number;
  committed: CareerUpConfirmationCommittedEntry[];
  declined: DeclinedKind;
} {
  const declined = declinedFromValues(values);
  const steps = buildSteps(values);

  if (declined === 'noCard') {
    return {
      stepIndex: 1,
      committed: [
        {
          stepIndex: 0,
          stepId: 'hasCard',
          rawValue: 'no',
          displayText: formatChoiceDisplay(t, 'hasCard', 'no'),
        },
      ],
      declined,
    };
  }

  if (declined === 'careerUpCardUploadNeeded') {
    const committed: CareerUpConfirmationCommittedEntry[] = [
      {
        stepIndex: 0,
        stepId: 'hasCard',
        rawValue: 'yes',
        displayText: formatChoiceDisplay(t, 'hasCard', 'yes'),
      },
      {
        stepIndex: 1,
        stepId: 'careerUpCardUploaded',
        rawValue: 'no',
        displayText: formatChoiceDisplay(t, 'careerUpCardUploaded', 'no'),
      },
    ];
    return { stepIndex: steps.length, committed, declined };
  }

  if (declined === 'driversLicenseUploadNeeded') {
    const committed: CareerUpConfirmationCommittedEntry[] = [
      {
        stepIndex: 0,
        stepId: 'hasCard',
        rawValue: 'yes',
        displayText: formatChoiceDisplay(t, 'hasCard', 'yes'),
      },
      {
        stepIndex: 1,
        stepId: 'careerUpCardUploaded',
        rawValue: 'yes',
        displayText: formatChoiceDisplay(t, 'careerUpCardUploaded', 'yes'),
      },
      {
        stepIndex: 2,
        stepId: 'driversLicenseUploaded',
        rawValue: 'no',
        displayText: formatChoiceDisplay(t, 'driversLicenseUploaded', 'no'),
      },
    ];
    return { stepIndex: steps.length, committed, declined };
  }

  if (currentStepId === DONE_STEP_ID) {
    const committed: CareerUpConfirmationCommittedEntry[] = steps.map((s, i) => {
      const raw = String(values[s.fieldName] ?? '').trim();
      return {
        stepIndex: i,
        stepId: s.stepId,
        rawValue: raw,
        displayText: formatChoiceDisplay(t, s.stepId, raw),
      };
    });
    return { stepIndex: steps.length, committed, declined: 'none' };
  }

  const idx = currentStepId ? steps.findIndex((s) => s.stepId === currentStepId) : -1;
  const stepIndex = idx >= 0 ? idx : 0;
  const committed: CareerUpConfirmationCommittedEntry[] = [];
  for (let i = 0; i < stepIndex; i++) {
    const s = steps[i];
    const raw = String(values[s.fieldName] ?? '').trim();
    committed.push({
      stepIndex: i,
      stepId: s.stepId,
      rawValue: raw,
      displayText: formatChoiceDisplay(t, s.stepId, raw),
    });
  }
  return { stepIndex, committed, declined: 'none' };
}

export function useCareerUpConfirmationFlow(
  currentEmail: string | null | undefined,
  invitationId: string | null | undefined,
  t: (key: string) => string
) {
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [values, setValues] = useState<CareerUpConfirmationFormValues>({});
  const [uploads, setUploads] = useState<CareerUpConfirmationUploadMeta>(EMPTY_UPLOADS);
  const [stepIndex, setStepIndex] = useState(0);
  const [committed, setCommitted] = useState<CareerUpConfirmationCommittedEntry[]>([]);
  const [declined, setDeclined] = useState<DeclinedKind>('none');
  const [assistantQuestionVisible, setAssistantQuestionVisible] = useState(true);
  const [editingCommitIndex, setEditingCommitIndex] = useState<number | null>(null);
  const [passwordDraft, setPasswordDraft] = useState('');
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const steps = useMemo(() => buildSteps(values), [values]);

  const currentStep = useMemo((): StepDef | null => {
    if (!steps.length) return null;
    if (editingCommitIndex !== null) return steps[editingCommitIndex] ?? null;
    if (stepIndex < steps.length) return steps[stepIndex];
    return null;
  }, [steps, stepIndex, editingCommitIndex]);

  const isComplete =
    declined === 'none' &&
    steps.length > 0 &&
    stepIndex >= steps.length &&
    editingCommitIndex === null;

  const noCardDeclined = declined === 'noCard';
  const careerUpCardUploadNeeded = declined === 'careerUpCardUploadNeeded';
  const driversLicenseUploadNeeded = declined === 'driversLicenseUploadNeeded';

  useEffect(() => {
    let cancelled = false;
    const email = currentEmail?.trim();
    const inv = invitationId?.trim();
    if (!email || !inv) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    void fetchCareerUpConfirmationProgress(email, inv).then((payload) => {
      if (cancelled) return;
      const v = payload?.values ?? {};
      setValues(v);
      setUploads(payload?.uploads ?? EMPTY_UPLOADS);
      const rebuilt = rebuildFromProgress(v, payload?.currentStepId, t);
      setStepIndex(rebuilt.stepIndex);
      setCommitted(rebuilt.committed);
      setDeclined(rebuilt.declined);
      setPasswordDraft(v.cardPassword ?? '');
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [currentEmail, invitationId, t]);

  const persist = useCallback(
    async (nextValues: CareerUpConfirmationFormValues, nextStepIndex: number, stepsSnapshot?: StepDef[]) => {
      const email = currentEmail?.trim();
      const inv = invitationId?.trim();
      if (!email || !inv) return null;
      const nextSteps = stepsSnapshot ?? buildSteps(nextValues);
      const cursor = cursorStepIdForSave(nextSteps, nextStepIndex);
      setSaveError(null);
      const saved = await upsertCareerUpConfirmationProgress({
        email,
        invitationId: inv,
        values: nextValues,
        currentStepId: cursor,
      });
      if (!saved) {
        setSaveError(t('submitDocuments.careerUpConfirmation.saveFailed'));
        return null;
      }
      setUploads(saved.uploads);
      return saved;
    },
    [currentEmail, invitationId, t]
  );

  const revealNextQuestion = useCallback(() => {
    setAssistantQuestionVisible(false);
    window.setTimeout(() => setAssistantQuestionVisible(true), NEXT_ASSISTANT_QUESTION_DELAY_MS);
  }, []);

  const finishEditStep = useCallback(
    async (
      entry: CareerUpConfirmationCommittedEntry,
      nextValues: CareerUpConfirmationFormValues,
      editedStepId: CareerUpConfirmationStep
    ) => {
      const cleared = clearDownstreamFields(nextValues, editedStepId);
      const nextCommitted = trimCommittedThrough(upsertCommittedEntry(committed, entry), editedStepId);
      const nextSteps = buildSteps(cleared);
      const editedOrdinal = stepOrdinal(editedStepId);

      let nextIdx = editedOrdinal + 1;
      while (nextIdx < nextSteps.length) {
        const s = nextSteps[nextIdx];
        const raw = String(cleared[s.fieldName] ?? '').trim();
        if (!raw) break;
        if (s.kind === 'choice' && raw !== 'yes' && raw !== 'no') break;
        nextIdx += 1;
      }

      setPasswordDraft(cleared.cardPassword ?? '');
      setValues(cleared);
      setCommitted(nextCommitted);
      setEditingCommitIndex(null);
      setDeclined('none');
      setStepIndex(nextIdx);
      await persist(cleared, nextIdx, nextSteps);
      revealNextQuestion();
    },
    [committed, persist, revealNextQuestion]
  );

  const advanceChoice = useCallback(
    async (stepId: 'hasCard' | 'careerUpCardUploaded' | 'driversLicenseUploaded', choice: YesNo) => {
      if (!choice) return;
      const fieldMap = {
        hasCard: 'hasCareerUpCard',
        careerUpCardUploaded: 'careerUpCardUploaded',
        driversLicenseUploaded: 'driversLicenseUploaded',
      } as const;
      const field = fieldMap[stepId];
      const entry: CareerUpConfirmationCommittedEntry = {
        stepIndex: editingCommitIndex ?? stepIndex,
        stepId,
        rawValue: choice,
        displayText: formatChoiceDisplay(t, stepId, choice),
      };
      const nextValues = { ...values, [field]: choice };

      if (stepId === 'hasCard' && choice === 'no') {
        if (editingCommitIndex !== null) setEditingCommitIndex(null);
        const saved = await persist(nextValues, 1);
        if (!saved) return;
        setValues(nextValues);
        setCommitted([entry]);
        setDeclined('noCard');
        setStepIndex(1);
        return;
      }

      if (stepId === 'careerUpCardUploaded' && choice === 'no') {
        if (editingCommitIndex !== null) setEditingCommitIndex(null);
        const saved = await persist(nextValues, buildSteps(nextValues).length);
        if (!saved) return;
        setValues(nextValues);
        setCommitted((prev) => trimCommittedThrough(upsertCommittedEntry(prev, entry), stepId));
        setDeclined('careerUpCardUploadNeeded');
        setStepIndex(buildSteps(nextValues).length);
        return;
      }

      if (stepId === 'driversLicenseUploaded' && choice === 'no') {
        if (editingCommitIndex !== null) setEditingCommitIndex(null);
        const saved = await persist(nextValues, buildSteps(nextValues).length);
        if (!saved) return;
        setValues(nextValues);
        setCommitted((prev) => trimCommittedThrough(upsertCommittedEntry(prev, entry), stepId));
        setDeclined('driversLicenseUploadNeeded');
        setStepIndex(buildSteps(nextValues).length);
        return;
      }

      if (editingCommitIndex !== null) {
        await finishEditStep(entry, nextValues, stepId);
        return;
      }

      const saved = await persist(nextValues, stepIndex + 1);
      if (!saved) return;
      setValues(nextValues);
      setCommitted((prev) => (stepId === 'hasCard' ? [entry] : upsertCommittedEntry(prev, entry)));
      setStepIndex((i) => i + 1);
      revealNextQuestion();
    },
    [values, persist, stepIndex, t, revealNextQuestion, editingCommitIndex, finishEditStep]
  );

  const advanceHasCard = useCallback((choice: YesNo) => advanceChoice('hasCard', choice), [advanceChoice]);
  const advanceCareerUpCardUploaded = useCallback(
    (choice: YesNo) => advanceChoice('careerUpCardUploaded', choice),
    [advanceChoice]
  );
  const advanceDriversLicenseUploaded = useCallback(
    (choice: YesNo) => advanceChoice('driversLicenseUploaded', choice),
    [advanceChoice]
  );

  const advancePassword = useCallback(async () => {
    const pwd = passwordDraft.trim();
    if (!pwd) return;
    const entry: CareerUpConfirmationCommittedEntry = {
      stepIndex: editingCommitIndex ?? stepIndex,
      stepId: 'cardPassword',
      rawValue: pwd,
      displayText: formatChoiceDisplay(t, 'cardPassword', pwd),
    };
    const nextValues = { ...values, cardPassword: pwd };

    if (editingCommitIndex !== null) {
      const nextCommitted = trimCommittedThrough(upsertCommittedEntry(committed, entry), 'cardPassword');
      const nextSteps = buildSteps(nextValues);
      setValues(nextValues);
      setCommitted(nextCommitted);
      setEditingCommitIndex(null);
      setStepIndex(nextSteps.length);
      await persist(nextValues, nextSteps.length, nextSteps);
      revealNextQuestion();
      return;
    }

    const saved = await persist(nextValues, steps.length);
    if (!saved) return;
    setValues(nextValues);
    setCommitted((prev) => upsertCommittedEntry(prev, entry));
    setStepIndex(steps.length);
    revealNextQuestion();
  }, [passwordDraft, values, persist, steps.length, t, revealNextQuestion, editingCommitIndex, stepIndex, committed]);

  const handleFinalComplete = useCallback(async () => {
    const email = currentEmail?.trim();
    const inv = invitationId?.trim();
    if (!email || !inv) return false;
    const saved = await upsertCareerUpConfirmationProgress({
      email,
      invitationId: inv,
      values,
      currentStepId: DONE_STEP_ID,
    });
    if (!saved) {
      setSaveError(t('submitDocuments.careerUpConfirmation.saveFailed'));
      return false;
    }
    setUploads(saved.uploads);
    return true;
  }, [currentEmail, invitationId, values, t]);

  const jumpToEditCommitIndex = useCallback(
    (commitIndex: number) => {
      if (!isComplete || commitIndex < 0 || commitIndex >= committed.length) return;
      const entry = committed[commitIndex];
      const stepIdx = steps.findIndex((s) => s.stepId === entry.stepId);
      if (stepIdx < 0) return;
      if (entry.stepId === 'cardPassword') {
        setPasswordDraft(values.cardPassword ?? entry.rawValue);
      }
      setEditingCommitIndex(stepIdx);
      setAssistantQuestionVisible(true);
    },
    [isComplete, committed, steps, values.cardPassword]
  );

  const undo = useCallback(() => {
    if (committed.length === 0) return;
    const last = committed[committed.length - 1];
    const next = committed.slice(0, -1);
    const nextStepIndex = next.length;

    setCommitted(next);
    setStepIndex(nextStepIndex);
    setEditingCommitIndex(null);
    setDeclined('none');

    const nextValues: CareerUpConfirmationFormValues = { ...values };
    if (last.stepId === 'hasCard') {
      delete nextValues.hasCareerUpCard;
      delete nextValues.careerUpCardUploaded;
      delete nextValues.driversLicenseUploaded;
      delete nextValues.cardPassword;
      setPasswordDraft('');
    } else if (last.stepId === 'careerUpCardUploaded') {
      delete nextValues.careerUpCardUploaded;
      delete nextValues.driversLicenseUploaded;
      delete nextValues.cardPassword;
      setPasswordDraft('');
    } else if (last.stepId === 'driversLicenseUploaded') {
      delete nextValues.driversLicenseUploaded;
      delete nextValues.cardPassword;
      setPasswordDraft('');
    } else if (last.stepId === 'cardPassword') {
      delete nextValues.cardPassword;
      setPasswordDraft('');
    }
    setValues(nextValues);
    void persist(nextValues, nextStepIndex);
    setAssistantQuestionVisible(true);
  }, [committed, values, persist]);

  const isDeclined = declined !== 'none';

  const showEditForCommitIndex = useCallback(
    (commitIndex: number) => {
      if (editingCommitIndex !== null) return false;
      if (isDeclined) return false;
      if (!isComplete || committed.length === 0) return false;
      return commitIndex >= 0 && commitIndex < committed.length;
    },
    [editingCommitIndex, isDeclined, isComplete, committed.length]
  );

  const showUndoForCommitIndex = useCallback(
    (commitIndex: number) => {
      if (editingCommitIndex !== null) return false;
      if (isDeclined) return commitIndex === committed.length - 1;
      if (isComplete && committed.length > 0) return commitIndex === committed.length - 1;
      return stepIndex === commitIndex + 1;
    },
    [editingCommitIndex, isDeclined, isComplete, committed.length, stepIndex]
  );

  return {
    t,
    isLoading,
    saveError,
    values,
    uploads,
    committed,
    isComplete,
    noCardDeclined,
    careerUpCardUploadNeeded,
    driversLicenseUploadNeeded,
    currentStep,
    assistantQuestionVisible,
    passwordDraft,
    setPasswordDraft,
    advanceHasCard,
    advanceCareerUpCardUploaded,
    advanceDriversLicenseUploaded,
    advancePassword,
    handleFinalComplete,
    jumpToEditCommitIndex,
    undo,
    showEditForCommitIndex,
    showUndoForCommitIndex,
    editingCommitIndex,
    threadEndRef,
  };
}
